import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCountry } from "@/context/CountryContext";
import { LogoMark, Wordmark } from "@/components/Reveal";
import { NAV_LINKS } from "@/data/site";

const CYCLE = ["AU", "US", "GB"];
const nextMarket = (code) => CYCLE[(CYCLE.indexOf(code) + 1) % CYCLE.length] || "AU";
const nextLabel = { AU: "United States · USD", US: "United Kingdom · GBP", GB: "Australia · AUD" };

const CountryPill = () => {
  const { market, currency, setCode } = useCountry();
  return (
    <button
      type="button"
      onClick={() => setCode(nextMarket(market.code))}
      data-testid="nav-country-pill"
      aria-label={`Shipping to ${market.name}. Switch country.`}
      title={`Switch to ${nextLabel[market.code] || "Australia · AUD"}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-tracksy-ink/15 bg-white/80 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-tracksy-ink backdrop-blur transition-colors hover:border-tracksy-orange hover:text-tracksy-orange"
    >
      <span className="text-sm leading-none">{market.flag}</span>
      {currency}
    </button>
  );
};

const Navbar = () => {
  const { count, setIsOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });
  const { pathname } = useLocation();
  const isFleet = pathname.startsWith("/fleet");

  return (
    <>
      <div
        data-testid="announcement-bar"
        className="flex items-center justify-center gap-2 bg-tracksy-dark px-4 py-2 text-center text-xs font-medium text-white sm:text-sm"
      >
        <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-tracksy-orange" />
        <span className="sm:hidden">Magnetic tracking for vehicles, trailers &amp; everyday gear</span>
        <span className="hidden sm:inline">Magnetic tracking for vehicles, trailers, fleets, equipment and everyday essentials</span>
      </div>

      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <motion.div className="absolute inset-x-0 top-0 h-0.5 origin-left bg-tracksy-orange" style={{ scaleX: progress }} />
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <LogoMark className="h-8 w-8" />
            <Wordmark />
          </Link>

          <div className="hidden items-center gap-6 xl:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-xs font-bold uppercase tracking-wider transition-colors duration-200 hover:text-tracksy-ink ${
                    isActive ? "text-tracksy-orange" : "text-tracksy-sub"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CountryPill />
            <Link
              to={isFleet ? "/fleet#enquiry" : "/shop"}
              data-testid="nav-shop-now"
              className="hidden rounded-full bg-tracksy-orange px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-tracksy-orangeHover sm:inline-flex"
            >
              {isFleet ? "Request pricing" : "Shop now"}
            </Link>
            {/* Compact mobile shop pill (icon only) — visible on narrow phones */}
            <Link
              to={isFleet ? "/fleet#enquiry" : "/shop"}
              data-testid="nav-shop-now-mobile"
              aria-label={isFleet ? "Request pricing" : "Shop Tracksy"}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-tracksy-orange text-white transition-colors hover:bg-tracksy-orangeHover sm:hidden"
            >
              <ShoppingBag className="h-4 w-4" />
            </Link>
            <button
              data-testid="cart-drawer-toggle"
              onClick={() => setIsOpen(true)}
              className="relative hidden h-11 items-center gap-2 rounded-full bg-tracksy-ink px-4 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] sm:inline-flex"
            >
              <ShoppingBag className="h-4 w-4" />
              <AnimatePresence mode="popLayout">
                <motion.span
                  key={count}
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  data-testid="cart-count-badge"
                  className="flex h-5 min-w-5 items-center justify-center rounded-full bg-tracksy-orange px-1 text-[11px] font-bold"
                >
                  {count}
                </motion.span>
              </AnimatePresence>
            </button>
            {/* Mobile cart (icon only, 44px touch target) */}
            <button
              data-testid="cart-drawer-toggle-mobile"
              onClick={() => setIsOpen(true)}
              aria-label="Open cart"
              className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-tracksy-ink text-white sm:hidden"
            >
              <ShoppingBag className="h-4 w-4" />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-tracksy-orange px-1 text-[10px] font-bold text-white">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-tracksy-ink xl:hidden"
              aria-label="Menu"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-black/5 bg-white xl:hidden"
            >
              <div className="flex flex-col gap-1 px-4 py-4" data-testid="mobile-menu">
                {NAV_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-base font-semibold text-tracksy-ink hover:bg-tracksy-cream"
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/shop"
                  onClick={() => setMenuOpen(false)}
                  className="mt-2 rounded-full bg-tracksy-orange px-5 py-3 text-center text-sm font-bold uppercase tracking-wider text-white"
                >
                  Shop now
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Navbar;
